import { useEffect, useMemo } from 'react';

import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { useToggle } from '~/eds/src/hooks/useToggle';
import { Filter } from '~/evifields/src/types/Filter';
import { Filters } from '~/features/advanced-search/filters/Filters';
import { useFieldsWithLazyLoad } from '~/features/filters/useFieldsWithLazyLoad';
import {
  attachFilterType,
  createRestrictedListFilters,
} from '~/features/filters/utils';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { usePinnedFilters } from '~/hooks/usePinnedFilters';
import { api } from '~/redux/api/createApi';

import { QueryBuilder } from '../query-builder/QueryBuilder';
import { ReadOnlyComplexQuery } from '../read-only-complex-query/ReadOnlyComplexQuery';
import { QueryFiltersProps, SearchQuery } from '../types';
import { buildFilterQuery } from '../utils/build-query';
import { getDefaultFilters } from '../utils/filters';
import { testIsSection } from '../utils/query';
import { API_PINNED_FILTERS_CONTEXT } from './contants';
import { mapQueryToFilters } from './QueryFilters.utils';

export const QueryFilters = ({
  api: apiParam = 'pilot',
  crossFilters = [],
  defaultFilters,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Temporarily ignoring this prop, using feature flag instead
  enableQueryBuilder: defaultEnableQueryBuilder = false,
  enablePinnedFilters = false,
  mode,
  placeholder,
  query,
  readOnly = false,
  restrictedFilterIds,
  onCrossFilterChange,
  onReset,
  onQueryChange,
}: QueryFiltersProps) => {
  const enableQueryBuilder = useFlag(FlagType.QueryBuilderEnabled);
  const enableDefaultFilters = query.length === 0 && defaultFilters;
  const [isQueryBuilderVisible, _toggle, showBuilder, hideBuilder] =
    useToggle(false);
  const isComplexQuery = useMemo(() => query.some(testIsSection), [query]);

  const { data: searchFiltersResults, isLoading: isFiltersLoading } =
    api.endpoints.getFilters.useQuery(undefined);

  const filtersArray = useMemo(() => {
    if (!searchFiltersResults) return [];
    let _searchFiltersResults = searchFiltersResults;
    if (restrictedFilterIds) {
      _searchFiltersResults = createRestrictedListFilters(
        searchFiltersResults,
        restrictedFilterIds,
      );
    }
    return Object.values(_searchFiltersResults);
  }, [restrictedFilterIds, searchFiltersResults]);

  const searchFilters = useFieldsWithLazyLoad({
    filters: filtersArray,
  });

  const queryToFilter = useMemo(() => {
    if (!searchFiltersResults) return [];
    return mapQueryToFilters(query, searchFiltersResults);
  }, [searchFiltersResults, query]);

  const searchFiltersMap = useMemo(
    () =>
      Object.fromEntries(searchFilters.map((filter) => [filter.id, filter])),
    [searchFilters],
  );

  const {
    getPinnedFilters,
    pinnedFiltersIds,
    isFetching: isPinnedFiltersLoading,
  } = usePinnedFilters(searchFiltersMap, API_PINNED_FILTERS_CONTEXT[apiParam]);

  const isLoading = isFiltersLoading || isPinnedFiltersLoading;

  useEffect(() => {
    if (enablePinnedFilters) {
      getPinnedFilters();
    }
  }, [enablePinnedFilters]);

  const defaultQuery = useMemo(() => {
    if (!searchFiltersResults) return [];
    const parsedDefaultFilters = getDefaultFilters(
      defaultFilters,
      searchFiltersResults,
    );

    const defaultFiltersWithTypes = attachFilterType(
      parsedDefaultFilters,
      searchFiltersResults,
    );
    return defaultFiltersWithTypes;
  }, [searchFiltersResults, defaultFilters]);

  const filters = enableDefaultFilters ? defaultQuery : queryToFilter;
  const handleChange = (updatedFilters: Filter[]) => {
    const updatedQuery = buildFilterQuery({
      booleanQuery: '',
      filters: updatedFilters,
      searchFilters: searchFiltersResults,
    });
    onQueryChange(updatedQuery);
  };

  const handleQueryBuilderSubmit = (query: SearchQuery) => {
    onQueryChange(query);
    hideBuilder();
  };

  const clearQuery = () => onQueryChange([]);

  const complexQueriesCallbacks = !readOnly
    ? { onClear: clearQuery, onEdit: showBuilder }
    : undefined;

  return (
    <ContentContainer
      loadingContent={{
        isLoading,
      }}
    >
      <>
        {!isComplexQuery ? (
          <Filters
            mode={mode}
            isLoading={isLoading}
            onChange={handleChange}
            placeholder={placeholder}
            readOnly={readOnly}
            selectedFilters={filters}
            searchFilters={searchFilters}
            onQueryButtonClick={enableQueryBuilder ? showBuilder : undefined}
            pinnedFiltersIds={
              enablePinnedFilters ? pinnedFiltersIds : undefined
            }
            onFiltersReset={onReset}
            onChangeCrossFilters={onCrossFilterChange}
            crossFilters={crossFilters}
          />
        ) : (
          <ReadOnlyComplexQuery
            searchFilters={searchFiltersMap}
            queryBuilder={query}
            {...complexQueriesCallbacks}
          />
        )}
        {isQueryBuilderVisible && (
          <QueryBuilder
            query={isComplexQuery ? query : []}
            onQueryChange={handleQueryBuilderSubmit}
            searchFilters={searchFilters}
            onHide={hideBuilder}
          />
        )}
      </>
    </ContentContainer>
  );
};
